import { format } from 'date-fns';

import type { InventoryRow } from '../types/inventoryRow';
import { POC_CURRENT_USER_DISPLAY_NAME } from '../hooks/useCurrentUserDisplayName';

const STORAGE_ROWS_KEY = 'poc-app-inventories-from-stock-rows-v1';
const STORAGE_SEQ_KEY = 'poc-app-inventory-auto-seq-v1';

export const POC_INVENTORIES_FROM_STOCK_EVENT = 'poc-inventories-from-stock-changed';

export interface StockLaunchSelectionItem {
  idStock: string;
  nomStock: string;
  siteAssocie: string;
  nombreArticles: number;
  quantiteTheorique: number;
  sommePaHt: number;
  sommePvTtc: number;
}

const toSafeString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const toSafeNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

function normalizeInventoryRow(row: Record<string, unknown>): InventoryRow {
  return {
    reference: toSafeString(row.reference),
    nom: toSafeString(row.nom),
    dateCreation: toSafeString(row.dateCreation),
    dateMiseAJour: toSafeString(row.dateMiseAJour),
    site: toSafeString(row.site),
    stock: toSafeString(row.stock),
    superviseur: toSafeString(row.superviseur),
    inventoriste: toSafeString(row.inventoriste),
    ouvertPar: toSafeString(row.ouvertPar, POC_CURRENT_USER_DISPLAY_NAME),
    emplacement: toSafeString(row.emplacement),
    nombreArticles: toSafeNumber(row.nombreArticles),
    quantiteTheorique: toSafeNumber(row.quantiteTheorique),
    quantiteComptee: toSafeNumber(row.quantiteComptee),
    sommePaHt: toSafeNumber(row.sommePaHt),
    sommePvTtc: toSafeNumber(row.sommePvTtc),
    ecart: toSafeNumber(row.ecart),
    statut:
      row.statut === 'Nouveau' || row.statut === 'En cours' || row.statut === 'Clôturé'
        ? row.statut
        : 'Nouveau',
    dateComptage: toSafeString(row.dateComptage),
  };
}

function readRows(): InventoryRow[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_ROWS_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return (parsed as Record<string, unknown>[]).map((row) => {
      const rowObj = row as Record<string, unknown>;

      if (rowObj.superviseur === undefined && typeof rowObj.manager === 'string') {
        const { manager, ...rest } = rowObj;

        return normalizeInventoryRow({ ...rest, superviseur: manager });
      }

      return normalizeInventoryRow(rowObj);
    });
  } catch {
    return [];
  }
}

function writeRows(rows: InventoryRow[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_ROWS_KEY, JSON.stringify(rows));
}

function nextReferenceNumber(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  const raw = window.localStorage.getItem(STORAGE_SEQ_KEY);

  if (raw === null) {
    window.localStorage.setItem(STORAGE_SEQ_KEY, '5');

    return 5;
  }

  const next = parseInt(raw, 10) + 1;

  window.localStorage.setItem(STORAGE_SEQ_KEY, String(next));

  return next;
}

function buildRowFromStockItem(
  item: StockLaunchSelectionItem,
  today: string,
  superviseur: string,
  ouvertPar: string,
): InventoryRow {
  const num = nextReferenceNumber();
  const quantiteComptee = 0;

  return {
    reference: `INV-2026-${String(num).padStart(3, '0')}`,
    nom: `Inventaire — ${item.nomStock}`,
    dateCreation: today,
    dateMiseAJour: today,
    site: item.siteAssocie,
    stock: item.nomStock,
    superviseur,
    inventoriste: '',
    ouvertPar,
    emplacement: '',
    nombreArticles: item.nombreArticles,
    quantiteTheorique: item.quantiteTheorique,
    quantiteComptee,
    sommePaHt: item.sommePaHt,
    sommePvTtc: item.sommePvTtc,
    ecart: quantiteComptee - item.quantiteTheorique,
    statut: 'Nouveau',
    dateComptage: '',
  };
}

export function loadStockLaunchedInventories(): InventoryRow[] {
  return readRows();
}

export function deleteStockLaunchedInventoryByReference(reference: string): void {
  const rows = readRows();
  const next = rows.filter((row) => row.reference !== reference);

  if (next.length === rows.length) {
    return;
  }

  writeRows(next);
}

export function appendInventoriesFromStock(
  items: StockLaunchSelectionItem[],
  superviseur: string,
  ouvertPar: string = POC_CURRENT_USER_DISPLAY_NAME,
): void {
  if (items.length === 0 || !superviseur.trim()) {
    return;
  }

  const existing = readRows();
  const today = format(new Date(), 'yyyy-MM-dd');
  const opener = ouvertPar.trim() || POC_CURRENT_USER_DISPLAY_NAME;
  const newRows = items.map((item) => buildRowFromStockItem(item, today, superviseur.trim(), opener));

  writeRows([...newRows, ...existing]);
}
